import * as R from 'remeda';
import { SchemaItem, SchemaTuple } from '@rossum/api-client/schemas';
import { FieldsFormModel } from '../formModels';
import {
  isAnyButtonFormModel,
  isAnySimpleValueFormModel,
  isCapturedFieldFormModel,
  isComputedFieldFormModel,
  isDataFieldFormModel,
  isDateFieldFormModel,
  isEnumFieldFormModel,
  isFormulaFieldFormModel,
  isLineItemButtonFormModel,
  isLineItemsFormModel,
  isLineItemSimpleValueFormModel,
  isLookupFieldFormModel,
  isManualFieldFormModel,
  isMultivalueFormModel,
  isNumberFieldFormModel,
  isReasoningFieldFormModel,
  isSectionFormModel,
  isStringFieldFormModel,
  isUnsetFieldFormModel,
  LineItemsFormModel,
  SectionFormModel,
} from '../formModels.guards';
import {
  extractCanExport,
  extractCanObtainToken,
  extractContext,
  extractDescription,
  extractExactLength,
  extractFormat,
  extractFormula,
  extractHidden,
  extractId,
  extractLabel,
  extractMatching,
  extractMaxLength,
  extractMemory,
  extractMinLength,
  extractOptions,
  extractPopupUrl,
  extractPrompt,
  extractRegexpPattern,
  extractRequired,
  extractRirFieldNames,
  extractScoreThreshold,
  extractType,
  extractUiEdit,
  extractUiType,
} from './toApiModel.extractors';
import { PropertyEvolver } from './toApiModel.utils';

const isSimpleValueLike = (blueprint: FieldsFormModel, inChildren?: boolean) =>
  isAnySimpleValueFormModel(blueprint) ||
  (isMultivalueFormModel(blueprint) && !!inChildren);

// `blueprint` refers to the fact we are "shaping" the resulting object based on what's contained
// in `blueprint`

// Evolvers for fields that can be in root or `children` of multivalue (a.k.a. `SchemaDatapoint` properties)
export const evolveCategory: PropertyEvolver<'category'> =
  (blueprint, inChildren) => fallback => {
    if (
      isSimpleValueLike(blueprint, inChildren) ||
      isAnyButtonFormModel(blueprint)
    ) {
      return 'datapoint';
    }
    if (isMultivalueFormModel(blueprint) || isLineItemsFormModel(blueprint)) {
      return 'multivalue';
    }
    if (isSectionFormModel(blueprint)) {
      return 'section';
    }
    return fallback;
  };

export const evolveId: PropertyEvolver<'id'> = blueprint => fallback =>
  extractId(fallback)(blueprint);

export const evolveLabel: PropertyEvolver<'label'> = blueprint => fallback =>
  extractLabel(fallback)(blueprint);

export const evolveDescription: PropertyEvolver<'description'> =
  blueprint => fallback =>
    extractDescription(fallback)(blueprint);

export const evolveHidden: PropertyEvolver<'hidden'> = blueprint => fallback =>
  extractHidden(fallback)(blueprint);

export const evolveCanExport: PropertyEvolver<'canExport'> =
  (blueprint, inChildren) => fallback => {
    if (
      isSimpleValueLike(blueprint, inChildren) ||
      isAnyButtonFormModel(blueprint)
    ) {
      return extractCanExport(fallback)(blueprint);
    }
    return undefined;
  };

export const evolveCanCollapse: PropertyEvolver<'canCollapse'> =
  blueprint => fallback => {
    if (
      isLineItemSimpleValueFormModel(blueprint) ||
      isLineItemButtonFormModel(blueprint)
    ) {
      return fallback;
    }
    return undefined;
  };

export const evolveRirFieldNames: PropertyEvolver<'rirFieldNames'> =
  (blueprint, inChildren) => fallback => {
    if (isAnyButtonFormModel(blueprint)) {
      return undefined;
    }
    if (isLineItemsFormModel(blueprint)) {
      return extractRirFieldNames(fallback)(blueprint);
    }
    if (isSimpleValueLike(blueprint, inChildren)) {
      if (
        isCapturedFieldFormModel(blueprint) ||
        isDataFieldFormModel(blueprint) ||
        isUnsetFieldFormModel(blueprint)
      ) {
        return extractRirFieldNames(fallback)(blueprint);
      }
      return [];
    }
    if (isMultivalueFormModel(blueprint) && !inChildren) {
      return undefined;
    }
    return [];
  };

export const evolveDefaultValue: PropertyEvolver<'defaultValue'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      return fallback;
    }
    return undefined;
  };

export const evolveConstraints: PropertyEvolver<'constraints'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      const stringConstraints = isStringFieldFormModel(blueprint)
        ? {
            length: {
              min: extractMinLength(fallback?.length?.min)(blueprint),
              max: extractMaxLength(fallback?.length?.max)(blueprint),
              exact: extractExactLength(fallback?.length?.exact)(blueprint),
            },
            regexp: {
              pattern: extractRegexpPattern(fallback?.regexp?.pattern)(
                blueprint
              ),
            },
          }
        : {};

      return {
        ...stringConstraints,
        required: extractRequired(fallback?.required)(blueprint),
      };
    }
    return undefined;
  };

export const evolveUiConfiguration: PropertyEvolver<'uiConfiguration'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      return {
        type: extractUiType(fallback?.type)(blueprint),
        edit: extractUiEdit(fallback?.edit)(blueprint),
      };
    }
    return undefined;
  };

export const evolveWidth: PropertyEvolver<'width'> = blueprint => fallback => {
  if (isLineItemSimpleValueFormModel(blueprint)) {
    return fallback;
  }
  return undefined;
};

export const evolveStretch: PropertyEvolver<'stretch'> =
  blueprint => fallback => {
    if (isLineItemSimpleValueFormModel(blueprint)) {
      return fallback;
    }
    return undefined;
  };

export const evolveWidthChars: PropertyEvolver<'widthChars'> =
  blueprint => fallback => {
    if (isLineItemSimpleValueFormModel(blueprint)) {
      return fallback;
    }
    return undefined;
  };

export const evolveScoreThreshold: PropertyEvolver<'scoreThreshold'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      if (
        isFormulaFieldFormModel(blueprint) ||
        isDataFieldFormModel(blueprint) ||
        isManualFieldFormModel(blueprint) ||
        isReasoningFieldFormModel(blueprint)
      ) {
        return 0;
      }
      if (
        isCapturedFieldFormModel(blueprint) ||
        isUnsetFieldFormModel(blueprint)
      ) {
        return extractScoreThreshold(fallback)(blueprint);
      }
    }
    return undefined;
  };

export const evolveFormula: PropertyEvolver<'formula'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      if (isFormulaFieldFormModel(blueprint)) {
        return extractFormula(fallback)(blueprint);
      }
    }
    return undefined;
  };

export const evolveContext: PropertyEvolver<'context'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      if (isReasoningFieldFormModel(blueprint)) {
        return extractContext(fallback)(blueprint);
      }
    }
    return undefined;
  };

export const evolvePrompt: PropertyEvolver<'prompt'> =
  (blueprint, inChildren) => fallback => {
    if (isSimpleValueLike(blueprint, inChildren)) {
      if (isReasoningFieldFormModel(blueprint)) {
        return extractPrompt(fallback)(blueprint);
      }
    }
    return undefined;
  };

export const evolveMatching: PropertyEvolver<'matching'> =
  (blueprint, inChildren) => fallback =>
    R.pipe(
      blueprint,
      R.conditional(
        [
          R.anyPass([
            isAnySimpleValueFormModel,
            R.allPass([isMultivalueFormModel, () => !!inChildren]),
          ]),
          R.piped(
            R.conditional(
              [isLookupFieldFormModel, extractMatching(fallback)],
              R.constant({})
            )
          ),
        ],
        R.constant(undefined)
      )
    );

export const evolveMemory: PropertyEvolver<'memory'> =
  (blueprint, inChildren) => fallback =>
    R.pipe(
      blueprint,
      R.conditional(
        [
          R.anyPass([
            isAnySimpleValueFormModel,
            R.allPass([isMultivalueFormModel, () => !!inChildren]),
          ]),
          R.piped(
            R.conditional(
              [isComputedFieldFormModel, extractMemory(fallback)],
              R.constant({})
            )
          ),
        ],
        R.constant(undefined)
      )
    );

export const evolveDisablePrediction: PropertyEvolver<'disablePrediction'> =
  blueprint => fallback => {
    if (
      (isAnySimpleValueFormModel(blueprint) ||
        isMultivalueFormModel(blueprint)) &&
      isCapturedFieldFormModel(blueprint)
    ) {
      return false;
    }
    if (
      isFormulaFieldFormModel(blueprint) ||
      isReasoningFieldFormModel(blueprint) ||
      isDataFieldFormModel(blueprint) ||
      isManualFieldFormModel(blueprint)
    ) {
      return true;
    }
    return fallback;
  };

export const evolveType: PropertyEvolver<'type'> =
  (blueprint, inChildren) => fallback => {
    if (isAnyButtonFormModel(blueprint)) {
      return 'button';
    }
    if (isSimpleValueLike(blueprint, inChildren)) {
      return extractType(fallback)(blueprint);
    }
    return undefined;
  };

export const evolveFormat: PropertyEvolver<'format'> =
  (blueprint, inChildren) => fallback => {
    if (
      isAnySimpleValueFormModel(blueprint) ||
      (isMultivalueFormModel(blueprint) && !!inChildren)
    ) {
      if (
        isNumberFieldFormModel(blueprint) ||
        isDateFieldFormModel(blueprint)
      ) {
        return extractFormat(fallback)(blueprint);
      }
      return undefined;
    }
    return undefined;
  };

export const evolveAggregations: PropertyEvolver<'aggregations'> =
  (blueprint, inChildren) => fallback => {
    if (
      isAnySimpleValueFormModel(blueprint) ||
      (isMultivalueFormModel(blueprint) && !!inChildren)
    ) {
      if (isNumberFieldFormModel(blueprint)) {
        return fallback;
      }
      return undefined;
    }
    return undefined;
  };

export const evolveOptions: PropertyEvolver<'options'> =
  (blueprint, inChildren) => fallback => {
    if (
      isAnySimpleValueFormModel(blueprint) ||
      (isMultivalueFormModel(blueprint) && !!inChildren)
    ) {
      if (isEnumFieldFormModel(blueprint)) {
        return extractOptions(fallback)(blueprint);
      }
    }
    return undefined;
  };

export const evolveEnumValueType: PropertyEvolver<'enumValueType'> =
  (blueprint, inChildren) => fallback => {
    if (
      isAnySimpleValueFormModel(blueprint) ||
      (isMultivalueFormModel(blueprint) && !!inChildren)
    ) {
      if (isEnumFieldFormModel(blueprint)) {
        return fallback;
      }
    }
    return undefined;
  };

export const evolvePopupUrl: PropertyEvolver<'popupUrl'> =
  blueprint => fallback => {
    if (isAnyButtonFormModel(blueprint)) {
      return extractPopupUrl(fallback)(blueprint);
    }
    return undefined;
  };

export const evolveCanObtainToken: PropertyEvolver<'canObtainToken'> =
  blueprint => fallback => {
    if (isAnyButtonFormModel(blueprint)) {
      return extractCanObtainToken(fallback)(blueprint);
    }
    return undefined;
  };

export const evolveGrid: PropertyEvolver<'grid'> = blueprint => fallback => {
  if (isMultivalueFormModel(blueprint) || isLineItemsFormModel(blueprint)) {
    return fallback;
  }
  return undefined;
};

export const evolveMinOccurrences: PropertyEvolver<'minOccurences'> =
  blueprint => fallback => {
    if (isMultivalueFormModel(blueprint) || isLineItemsFormModel(blueprint)) {
      return fallback;
    }
    return undefined;
  };

export const evolveMaxOccurrences: PropertyEvolver<'maxOccurences'> =
  blueprint => fallback => {
    if (isMultivalueFormModel(blueprint) || isLineItemsFormModel(blueprint)) {
      return fallback;
    }
    return undefined;
  };

export const evolveShowGridByDefault: PropertyEvolver<'showGridByDefault'> =
  blueprint => fallback => {
    if (isMultivalueFormModel(blueprint) || isLineItemsFormModel(blueprint)) {
      return fallback;
    }
    return undefined;
  };

const makeEvolverWithoutChildren =
  (inChildren: boolean) => (blueprint: FieldsFormModel) => {
    return {
      category: evolveCategory(blueprint, inChildren),
      id: evolveId(blueprint),
      label: evolveLabel(blueprint, inChildren),
      description: evolveDescription(blueprint, inChildren),
      hidden: evolveHidden(blueprint, inChildren),
      canExport: evolveCanExport(blueprint, inChildren),
      type: evolveType(blueprint, inChildren),
      constraints: evolveConstraints(blueprint, inChildren),
      options: evolveOptions(blueprint, inChildren),
      enumValueType: evolveEnumValueType(blueprint, inChildren),
      format: evolveFormat(blueprint, inChildren),
      uiConfiguration: evolveUiConfiguration(blueprint, inChildren),
      rirFieldNames: evolveRirFieldNames(blueprint, inChildren),
      scoreThreshold: evolveScoreThreshold(blueprint, inChildren),
      formula: evolveFormula(blueprint, inChildren),
      popupUrl: evolvePopupUrl(blueprint, inChildren),
      canObtainToken: evolveCanObtainToken(blueprint, inChildren),
      defaultValue: evolveDefaultValue(blueprint, inChildren),
      canCollapse: evolveCanCollapse(blueprint, inChildren),
      width: evolveWidth(blueprint, inChildren),
      stretch: evolveStretch(blueprint, inChildren),
      widthChars: evolveWidthChars(blueprint, inChildren),
      disablePrediction: evolveDisablePrediction(blueprint, inChildren),
      aggregations: evolveAggregations(blueprint, inChildren),
      minOccurrences: evolveMinOccurrences(blueprint, inChildren),
      maxOccurrences: evolveMaxOccurrences(blueprint, inChildren),
      grid: evolveGrid(blueprint, inChildren),
      showGridByDefault: evolveShowGridByDefault(blueprint, inChildren),
      prompt: evolvePrompt(blueprint, inChildren),
      matching: evolveMatching(blueprint, inChildren),
      context: evolveContext(blueprint, inChildren),
      memory: evolveMemory(blueprint, inChildren),
    };
  };

const evolveLineItemChildren =
  (blueprint: LineItemsFormModel) => (fallback: SchemaTuple) =>
    R.merge(fallback, {
      id: fallback.id || evolveId(blueprint)(fallback.id),
      label: fallback.label || evolveLabel(blueprint, true)(fallback.label),
      description:
        fallback.description ||
        evolveDescription(blueprint, true)(fallback.description),
    });

const evolveSectionChildren =
  (_blueprint: SectionFormModel) => (fallback: SchemaItem[]) =>
    fallback;

const makeOnlyChildrenEvolver = (blueprint: FieldsFormModel) => {
  if (isLineItemsFormModel(blueprint)) {
    return { children: evolveLineItemChildren(blueprint) };
  }
  if (isSectionFormModel(blueprint)) {
    return { children: evolveSectionChildren(blueprint) };
  }
  if (isMultivalueFormModel(blueprint)) {
    return { children: makeEvolverWithoutChildren(true)(blueprint) };
  }
  return {};
};

export const makeEvolver = (blueprint: FieldsFormModel) =>
  R.mergeAll([
    makeEvolverWithoutChildren(false)(blueprint),
    makeOnlyChildrenEvolver(blueprint),
  ]);
